import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { inboxService } from '@/services/inboxService';
import { useToken } from './TokenContext';

const POLLING_INTERVAL = 60000;

const InboxContext = createContext({
  unreadCount: 0,
  notifications: [],
  loading: false,
  error: null,
  hasMore: false,
  fetchNotifications: () => {},
  fetchUnreadCount: () => {},
  markAsRead: () => {},
  markAllAsRead: () => {},
  refresh: () => {},
});

export const InboxProvider = ({ children }) => {
  const { idToken } = useToken();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);

  const pollingRef = useRef(null);
  const notificationsRef = useRef(notifications);
  notificationsRef.current = notifications;

  const fetchUnreadCount = useCallback(async () => {
    if (!idToken) return;
    try {
      const count = await inboxService.getUnreadCount();
      setUnreadCount(count);
    } catch (err) {
      console.error('[inbox] Erro ao buscar unread count:', err);
    }
  }, [idToken]);

  const fetchNotifications = useCallback(
    async (pageNum = 1, reset = false, unreadOnly = false) => {
      if (!idToken) return;
      setLoading(true);
      setError(null);
      try {
        const response = await inboxService.getNotifications({
          page: pageNum,
          pageSize: 10,
          unreadOnly,
        });

        if (reset) {
          setNotifications(response.items);
        } else {
          const existingIds = new Set(notificationsRef.current.map((n) => n.id));
          const newItems = response.items.filter((item) => !existingIds.has(item.id));
          setNotifications((prev) => [...prev, ...newItems]);
        }

        setHasMore(response.hasMore);
        setPage(pageNum);
      } catch (err) {
        console.error('[inbox] Erro ao buscar notificações:', err);
        setError('Erro ao carregar notificações');
      } finally {
        setLoading(false);
      }
    },
    [idToken]
  );

  const markAsRead = useCallback(async (notificationId) => {
    const prevNotifications = notificationsRef.current;

    setNotifications((prev) =>
      prev.map((n) => (n.id === notificationId ? { ...n, isRead: true } : n))
    );

    try {
      await inboxService.markAsRead(notificationId);
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (err) {
      console.error('[inbox] Erro ao marcar como lida:', err);
      setNotifications(prevNotifications);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    const prevNotifications = notificationsRef.current;
    const prevCount = unreadCount;

    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    setUnreadCount(0);

    try {
      await inboxService.markAllAsRead();
    } catch (err) {
      console.error('[inbox] Erro ao marcar todas como lidas:', err);
      setNotifications(prevNotifications);
      setUnreadCount(prevCount);
    }
  }, [unreadCount]);

  const refresh = useCallback(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  // Polling de unread count
  useEffect(() => {
    if (!idToken) {
      setUnreadCount(0);
      return;
    }
    fetchUnreadCount();
    pollingRef.current = setInterval(fetchUnreadCount, POLLING_INTERVAL);
    return () => clearInterval(pollingRef.current);
  }, [idToken, fetchUnreadCount]);

  // Escuta evento de push recebido para atualizar badge
  useEffect(() => {
    const handler = () => fetchUnreadCount();
    window.addEventListener('inbox:refresh', handler);
    return () => window.removeEventListener('inbox:refresh', handler);
  }, [fetchUnreadCount]);

  // Cleanup em logout
  useEffect(() => {
    const handler = () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      setUnreadCount(0);
      setNotifications([]);
    };
    window.addEventListener('user:logout', handler);
    return () => window.removeEventListener('user:logout', handler);
  }, []);

  return (
    <InboxContext.Provider
      value={{
        unreadCount,
        notifications,
        loading,
        error,
        hasMore,
        page,
        fetchNotifications,
        fetchUnreadCount,
        markAsRead,
        markAllAsRead,
        refresh,
      }}
    >
      {children}
    </InboxContext.Provider>
  );
};

export const useInbox = () => useContext(InboxContext);
